// Pantalla de confirmación después de crear un impeachment (versión web).
// Ofrece la opción de bloquear al usuario reportado.
// Adaptado para web desde amarsi-app (React Native a React DOM).

import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuthQuery } from '../../hooks/useAuthQuery';
import { useBlockUserMutation } from '../../hooks/useFriendMutations';
import MegaButton from '../../components/UI/MegaButton';
import { MdCheckCircle, MdInfo, MdShield } from 'react-icons/md';

export default function AfterCreateImpeachmentScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const targetUser = location.state || { full_name: 'el usuario' };

  // Obtiene la sesión del usuario
  const { sessionQuery } = useAuthQuery();
  const session = sessionQuery.data?.session;
  const user = session?.user;

  // Estado local para controlar el loading
  const [isBlocking, setIsBlocking] = useState(false);

  // Hook para mutación de bloqueo de usuario
  const blockUserMutation = useBlockUserMutation();

  // Maneja el bloqueo del usuario usando TanStack Query
  const handleBlockUser = async () => {
    if (!user?.id || !targetUser?.id) {
      alert('Error: información de usuario incompleta');
      return;
    }

    try {
      setIsBlocking(true);
      
      await blockUserMutation.mutateAsync({
        gestorId: user.id,
        bloqueadoId: targetUser.id,
      });

      alert(`Bloqueaste a ${targetUser.full_name}`);

      // Navegar de regreso a la pantalla inicial después de bloquear
      navigate('/radar');

    } catch (error) {
      alert(`Error al bloquear usuario: ${error.message || 'Error desconocido'}`);
    } finally {
      setIsBlocking(false);
    }
  };

  // Navegación a la pantalla inicial
  const navigateToScreenBase = () => {
    navigate('/radar');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-neutral-900 via-neutral-800 to-neutral-900 flex flex-col">
      {/* Header con gradiente */}
      <div className="h-20 border-b border-neutral-700 bg-neutral-900/50 backdrop-blur-sm flex items-center justify-center gap-4">
        <MdCheckCircle className="w-6 h-6 text-green-500" />
        <h1 className="text-white text-xl font-semibold">Reporte enviado</h1>
      </div>

      {/* Contenido */}
      <div className="flex-1 overflow-y-auto p-6 max-w-2xl mx-auto w-full">
        {/* Card de agradecimiento */}
        <div className="bg-neutral-800/50 backdrop-blur-sm rounded-2xl p-6 mb-6 border border-neutral-700 shadow-xl">
          <div className="flex items-start gap-4 mb-4">
            <div className="bg-green-500/20 p-3 rounded-full">
              <MdCheckCircle className="w-8 h-8 text-green-500" />
            </div>
            <div className="flex-1">
              <h2 className="text-white text-2xl font-bold mb-3 leading-tight">
                Gracias por ayudarnos a que Amarsi sea mejor para todos
              </h2>
              <p className="text-neutral-400 text-base leading-relaxed">
                Sabemos que no fue fácil y valoramos que te hayas tomado el tiempo de responder a esas preguntas.
              </p>
            </div>
          </div>
        </div>
        
        {/* Card de información del proceso */}
        <div className="bg-neutral-800/50 backdrop-blur-sm rounded-2xl p-6 mb-6 border border-neutral-700 shadow-xl">
          <div className="flex items-start gap-4 mb-4">
            <div className="bg-blue-500/20 p-3 rounded-full">
              <MdInfo className="w-8 h-8 text-blue-500" />
            </div>
            <div className="flex-1">
              <h2 className="text-white text-xl font-bold mb-3">
                ¿Qué sucede a continuación?
              </h2>
              <p className="text-neutral-400 text-base leading-relaxed">
                Usaremos un proceso automatizado para analizar esta cuenta. La automatización nos permite administrar mejor los cientos de millones de denuncias que recibimos cada año. Además, usamos esta información para mejorar nuestra plataforma y mostrarte menos contenido de este tipo.
              </p>
            </div>
          </div>
        </div>

        {/* Card de acciones recomendadas */}
        <div className="bg-neutral-800/50 backdrop-blur-sm rounded-2xl p-6 mb-6 border border-neutral-700 shadow-xl">
          <div className="flex items-start gap-4 mb-4">
            <div className="bg-purple-500/20 p-3 rounded-full">
              <MdShield className="w-8 h-8 text-purple-500" />
            </div>
            <div className="flex-1">
              <h2 className="text-white text-xl font-bold mb-3">
                Otras cosas que puede hacer mientras tanto
              </h2>
              <p className="text-neutral-400 text-base leading-relaxed">
                Bloquea la cuenta {targetUser.full_name} para que no pueda verte ni enviarte mensajes. Si la bloqueas, tampoco verás a de esta cuenta.
              </p>
            </div>
          </div>
        </div>

        {/* Botón para bloquear usuario con diseño mejorado */}
        <div className="mb-6">
          <MegaButton
            labelText={`Bloquear a ${targetUser.full_name}`}
            onPress={handleBlockUser}
            loading={isBlocking || blockUserMutation.isPending}
            disabled={isBlocking || blockUserMutation.isPending}
            className="w-full !bg-neutral-800 border border-neutral-600 text-white hover:!bg-neutral-500"
          />
        </div>
      </div>

      {/* Botón para finalizar con diseño mejorado */}
      <div className="h-16 flex items-center justify-end bg-neutral-900 px-4 border-t border-neutral-700">
        <MegaButton
          labelText="Listo"
          onPress={navigateToScreenBase}
          loading={false}
          disabled={false}
          className="!h-12 !py-0"
        />
      </div>
    </div>
  );
}
